import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";

export default function Progress() {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/sessions/me"), api.get("/sessions/me/stats")]).then(([s, st]) => {
      setSessions(s.data);
      setStats(st.data);
    });
  }, []);

  const chartData = [...sessions].reverse().map((s, i) => ({
    name: `#${i + 1}`,
    stressBefore: s.stress_before,
    stressAfter: s.stress_after,
    focusBefore: s.focus_before,
    focusAfter: s.focus_after,
    energyBefore: s.energy_before,
    energyAfter: s.energy_after,
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-12">
      <p className="overline text-primary mb-2">Progress</p>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">Your nervous system, over time</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">Track shifts in stress, focus, energy, and recovery as you train.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        <Stat label="Total sessions" value={stats?.total_sessions ?? 0} />
        <Stat label="Avg stress reduction" value={stats?.avg_stress_reduction ?? 0} suffix=" pts" />
        <Stat label="Avg energy gain" value={stats?.avg_energy_gain ?? 0} suffix=" pts" />
        <Stat label="Avg focus gain" value={stats?.avg_focus_gain ?? 0} suffix=" pts" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Card className="p-6">
          <p className="overline text-primary mb-1">Stress</p>
          <h3 className="font-display text-xl tracking-tight mb-4">Before vs After</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey="stressBefore" fill="hsl(var(--secondary))" name="Before" />
                <Bar dataKey="stressAfter" fill="hsl(var(--primary))" name="After" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6">
          <p className="overline text-primary mb-1">Focus</p>
          <h3 className="font-display text-xl tracking-tight mb-4">Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="focusBefore" stroke="hsl(var(--secondary))" name="Before" strokeWidth={2} />
                <Line type="monotone" dataKey="focusAfter" stroke="hsl(var(--primary))" name="After" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-10">
        <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mb-4">All sessions</h2>
        <Card className="p-0 overflow-hidden">
          {sessions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No sessions logged yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]" data-testid="progress-sessions-table">
                <thead className="bg-muted">
                  <tr className="text-left">
                    <Th>Date</Th><Th>Practice</Th><Th>Stress</Th><Th>Energy</Th><Th>Focus</Th><Th>Sleep</Th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-t border-border">
                      <Td>{new Date(s.completed_at).toLocaleDateString()}</Td>
                      <Td>{s.practice_title}</Td>
                      <Td>{s.stress_before} → <span className="text-primary font-medium">{s.stress_after}</span></Td>
                      <Td>{s.energy_before} → <span className="text-primary font-medium">{s.energy_after}</span></Td>
                      <Td>{s.focus_before} → <span className="text-primary font-medium">{s.focus_after}</span></Td>
                      <Td>{s.sleep_quality ?? "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;

function Stat({ label, value, suffix = "" }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="p-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-2 font-display text-3xl font-semibold tracking-tight">{value}{suffix}</p>
      </Card>
    </motion.div>
  );
}
